import { useCallback, useContext, useState } from "react"

import { useLocalSearchParams } from "expo-router"

import {
  AccountCollection,
  AccountData,
  ClientCollection,
  Link,
  RecurringContribution,
  RecurringContributionCollection,
} from "wealthmode-frontend/backend"
import { Toggle } from "wealthmode-frontend/components"
import { Box, HStack, VStack } from "wealthmode-frontend/gluestack/ui"
import { formatMoneyFull, formatPercentage } from "wealthmode-frontend/util"

import { DataTable } from "@/components"
import { BackendContext } from "@/context"

const STATUS_OPTIONS = [
  { id: "active", text: "Active" },
  { id: "expired", text: "Expired" },
]

const STATUS_WHERE: Record<string, string> = {
  active: "eq(end_date,null)",
  expired: "neq(end_date,null)",
}

export default function RecurringContributions() {
  const { client_id, account_id } = useLocalSearchParams<{
    client_id: string
    account_id: string
  }>()

  const backend = useContext(BackendContext)

  const [status, setStatus] = useState("active")

  const load = useCallback(async () => {
    const clients = backend.collection("clients") as ClientCollection
    const client = clients.model(client_id)
    await client.fetch({ reload: true })

    const accounts = client.link("accounts")?.resolve() as AccountCollection
    const account = accounts.model(account_id)
    await account.fetch({ reload: true })

    const data = account.embed("data") as AccountData
    const link = data.link("recurring_contributions")
    if (link == null || link.type == null) {
      return undefined
    }

    // A Link memoizes what it resolves to, so switching status needs a
    // collection of its own. The clause goes in the URL rather than in fetch
    // options so reloads and pagination keep it.
    const href = new URL(link.href.toString())
    href.searchParams.set("where", STATUS_WHERE[status])

    const contributions = new Link(href, link.type).resolve() as RecurringContributionCollection
    await contributions.fetch({ reload: true })

    return contributions
  }, [backend, client_id, account_id, status])

  return (
    <Box>
      <VStack className="flex-1 gap-4">
        <HStack className="items-center justify-end">
          <Toggle
            options={STATUS_OPTIONS}
            value={status}
            onChange={(option) => setStatus(option.id)}
          />
        </HStack>

        <HStack>
          <Box className="flex-1">
            <DataTable
              title={"Recurring Contributions"}
              load={load}
              header={[
                { id: "id", title: "ID", shrink: true },
                { id: "frequency", title: "Frequency" },
                { id: "interval", title: "Interval", shrink: true },
                { id: "start_date", title: "Start" },
                { id: "end_date", title: "End" },
                { id: "amount", title: "Amount" },
                { id: "allocations", title: "Allocations" },
              ]}
              render={(model: RecurringContribution, field: string) => {
                switch (field) {
                  case "amount":
                    return formatMoneyFull(model.get(field))

                  case "allocations": {
                    const allocations = model.allocations()
                    if (allocations.length === 0) {
                      return null
                    }
                    return allocations
                      .map((allocation) => {
                        const sec = allocation.instrument()
                        const symbol =
                          sec?.attributes.symbol ??
                          (sec?.attributes.type == "cash" ? "Cash" : sec?.attributes.type) ??
                          "?"
                        return `${symbol} ${formatPercentage(allocation.attributes.percentage)}`
                      })
                      .join(", ")
                  }
                }
                return model.get(field)
              }}
            />
          </Box>
        </HStack>
      </VStack>
    </Box>
  )
}
